import { useQuery } from '@tanstack/react-query';
import type { ContractDto, ExpenseDto, FinanceOverviewDto, InvoiceDto } from '@cw/shared';
import { api } from './api';

export const financeKeys = {
  all: ['finance'] as const,
  overview: (m: string) => ['finance', 'overview', m] as const,
  invoices: (qs: string) => ['finance', 'invoices', qs] as const,
  invoice: (id: string) => ['finance', 'invoice', id] as const,
  contracts: (qs: string) => ['finance', 'contracts', qs] as const,
  expenses: (qs: string) => ['finance', 'expenses', qs] as const,
  categories: ['finance', 'categories'] as const,
};

const qs = (f: Record<string, string | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) p.set(k, v);
  return p.toString();
};

export const useFinanceOverview = (month: string) =>
  useQuery({
    queryKey: financeKeys.overview(month),
    queryFn: () => api.get<FinanceOverviewDto>(`/finance/overview?month=${month}`),
  });
export function useInvoices(f: { clientId?: string; status?: string } = {}) {
  const q = qs(f);
  return useQuery({
    queryKey: financeKeys.invoices(q),
    queryFn: () => api.get<InvoiceDto[]>(`/finance/invoices${q ? `?${q}` : ''}`),
  });
}
export const useInvoice = (id: string | null) =>
  useQuery({
    queryKey: financeKeys.invoice(id ?? ''),
    queryFn: () => api.get<InvoiceDto>(`/finance/invoices/${id}`),
    enabled: Boolean(id),
  });
export function useContracts(f: { clientId?: string } = {}) {
  const q = qs(f);
  return useQuery({
    queryKey: financeKeys.contracts(q),
    queryFn: () => api.get<ContractDto[]>(`/finance/contracts${q ? `?${q}` : ''}`),
  });
}
export function useExpenses(f: { from?: string; to?: string } = {}) {
  const q = qs(f);
  return useQuery({
    queryKey: financeKeys.expenses(q),
    queryFn: () => api.get<ExpenseDto[]>(`/finance/expenses${q ? `?${q}` : ''}`),
  });
}
export const useExpenseCategories = () =>
  useQuery({
    queryKey: financeKeys.categories,
    queryFn: () => api.get<{ id: string; name: string; color: string }[]>('/finance/expense-categories'),
    staleTime: 300_000,
  });

/** "1.234,56" ou "1234.56" → centavos. */
export function parseMoney(v: string): number | null {
  const clean = v.replace(/[^\d,.-]/g, '');
  if (!clean) return null;
  const normalized = clean.includes(',') ? clean.replace(/\./g, '').replace(',', '.') : clean;
  const n = Number(normalized);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}
export const moneyInput = (cents: number | null | undefined) =>
  cents == null
    ? ''
    : (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const isoToday = () => new Date().toISOString().slice(0, 10);
export const fmtDate = (d: string) =>
  new Date(`${d}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
